import React, { useEffect, useState } from "react";
import moment from "moment";
import { CircularProgress } from "@mui/material";
import { createComplaint, getMyClinics } from "../../api/feedback";
import {
  COMPLAINT_CATEGORIES,
  complaintTargetToBody,
  validateComplaint,
} from "./feedbackUtils";

/**
 * "Raise a complaint": pick a visit (sets appointmentId) or a clinic
 * (clinicId), category, subject, description, self/family → POST →
 * `onCreated(result)` with `{ message, referenceId, complaint }`.
 *
 * `visits` come from the overview; clinics are loaded here from
 * GET /patient-feedback/me/clinics. `initialTarget` pre-selects a visit
 * ("visit:<appointmentId>") for the deep link.
 */
const ComplaintForm = ({ visits = [], initialTarget = "", onCreated, onCancel }) => {
  const [clinics, setClinics] = useState([]);
  const [target, setTarget] = useState(initialTarget);
  const [category, setCategory] = useState("care");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [relation, setRelation] = useState("self");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    getMyClinics()
      .then((data) => {
        if (alive) setClinics(Array.isArray(data?.clinics) ? data.clinics : []);
      })
      .catch((err) => {
        // The visit picker still works without the clinic list.
        if (alive && visits.length === 0) setError(err?.message || "");
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onSubmit(e) {
    e.preventDefault();
    const problem = validateComplaint({ target, category, subject, description });
    if (problem) {
      setError(problem);
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const result = await createComplaint({
        ...complaintTargetToBody(target),
        category,
        subject: subject.trim(),
        description: description.trim(),
        relation,
      });
      onCreated(result);
    } catch (err) {
      setError(err?.message || "Could not register your complaint");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="fb_card" onSubmit={onSubmit} noValidate>
      <h2 className="fb_survey_title">Raise a complaint</h2>
      <p className="fb_muted">
        Tell the clinic what went wrong. You will get a reference number and
        updates as they work on it.
      </p>

      <label className="fb_label" htmlFor="fb_target">
        This complaint is about
      </label>
      <select
        id="fb_target"
        className="fb_select"
        value={target}
        onChange={(e) => setTarget(e.target.value)}
      >
        <option value="">Select a visit or clinic</option>
        {visits.length > 0 ? (
          <optgroup label="Your visits">
            {visits.map((v) => (
              <option key={v.appointmentId} value={`visit:${v.appointmentId}`}>
                {[v.clinicName, v.doctorName].filter(Boolean).join(" · ")}
                {v.date ? ` · ${moment(v.date).format("DD MMM YYYY")}` : ""}
              </option>
            ))}
          </optgroup>
        ) : null}
        {clinics.length > 0 ? (
          <optgroup label="Clinics">
            {clinics.map((c) => (
              <option key={c._id} value={`clinic:${c._id}`}>
                {c.name}
                {c.city ? `, ${c.city}` : ""}
              </option>
            ))}
          </optgroup>
        ) : null}
      </select>

      <label className="fb_label" htmlFor="fb_category">
        Category
      </label>
      <select
        id="fb_category"
        className="fb_select"
        value={category}
        onChange={(e) => setCategory(e.target.value)}
      >
        {COMPLAINT_CATEGORIES.map((c) => (
          <option key={c.value} value={c.value}>
            {c.label}
          </option>
        ))}
      </select>

      <label className="fb_label" htmlFor="fb_subject">
        Subject
      </label>
      <input
        id="fb_subject"
        className="fb_input"
        type="text"
        maxLength={200}
        placeholder="In a few words"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
      />

      <label className="fb_label" htmlFor="fb_description">
        Description
      </label>
      <textarea
        id="fb_description"
        className="fb_textarea"
        rows={5}
        maxLength={5000}
        placeholder="What happened, when, and what you would like the clinic to do (at least 10 characters)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />

      <fieldset className="fb_fieldset">
        <legend className="fb_label">This is for</legend>
        <label className="fb_radio">
          <input
            type="radio"
            name="relation"
            value="self"
            checked={relation === "self"}
            onChange={() => setRelation("self")}
          />{" "}
          Myself
        </label>
        <label className="fb_radio">
          <input
            type="radio"
            name="relation"
            value="family"
            checked={relation === "family"}
            onChange={() => setRelation("family")}
          />{" "}
          A family member
        </label>
      </fieldset>

      {error ? (
        <p className="fb_error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="fb_actions">
        <button
          type="button"
          className="fb_btn_secondary"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
        <button type="submit" className="fb_btn_primary" disabled={submitting}>
          {submitting ? (
            <CircularProgress sx={{ color: "white" }} size="1rem" />
          ) : (
            "Submit complaint"
          )}
        </button>
      </div>
    </form>
  );
};

export default ComplaintForm;
